import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Modal,
  Form,
  InputGroup,
  Alert,
  Row,
  Col,
  Tabs,
  Tab,
} from "react-bootstrap";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import { useAuth } from "../context/AuthProvider";
import {
  getAllStudentFees,
  getAllPayments,
  getFeeStatistics,
  recordPayment,
  getStudentFeeRecord,
  getStudentPaymentHistory,
  initializeStudentFee,
  applyDiscount,
  removeDiscount,
  PAYMENT_STATUS,
  PAYMENT_METHODS,
  formatCurrency,
  getPaymentStatusColor,
  getPaymentStatusDisplay,
  checkOverdueFees,
} from "../services/studentFeeService";
import { getStudents } from "../services/userService";
import { STUDENT_LEVELS } from "../utils";
import { Search, DollarSign, Users, TrendingUp, AlertCircle } from "lucide-react";

export default function FeeManagement() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [studentFees, setStudentFees] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [feeStats, setFeeStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Payment Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: "",
    paymentMethod: PAYMENT_METHODS.CASH,
    transactionId: "",
    notes: "",
    receivedBy: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Initialize Fee Modal
  const [showInitModal, setShowInitModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Discount Modal
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountData, setDiscountData] = useState({
    discountType: "percentage",
    discountValue: "",
    discountReason: "",
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [fees, payments, stats, allStudents] = await Promise.all([
        getAllStudentFees(),
        getAllPayments(),
        getFeeStatistics(),
        getStudents(),
      ]);

      setStudentFees(fees);
      setAllPayments(payments);
      setFeeStats(stats);
      setStudents(allStudents);

      // Check for overdue fees
      await checkOverdueFees();
    } catch (error) {
      console.error("Error fetching fee data:", error);
      toast.error("Failed to fetch fee data");
    }
    setLoading(false);
  };

  const handleOpenPaymentModal = async (fee) => {
    setSelectedFee(fee);
    setPaymentData({
      amount: fee.installmentAmount.toString(),
      paymentMethod: PAYMENT_METHODS.CASH,
      transactionId: `TXN-${Date.now()}`,
      notes: "",
      receivedBy: user?.name || "Admin",
    });
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedFee(null);
    setPaymentData({
      amount: "",
      paymentMethod: PAYMENT_METHODS.CASH,
      transactionId: "",
      notes: "",
      receivedBy: "",
    });
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFee) {
      toast.error("No fee record selected");
      return;
    }

    const amount = parseFloat(paymentData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (amount > selectedFee.remainingAmount) {
      toast.error(`Amount cannot exceed remaining balance: ${formatCurrency(selectedFee.remainingAmount)}`);
      return;
    }

    setSubmitting(true);
    try {
      await recordPayment({
        studentId: selectedFee.studentId,
        feeId: selectedFee.id,
        amount: amount,
        paymentMethod: paymentData.paymentMethod,
        transactionId: paymentData.transactionId,
        notes: paymentData.notes,
        receivedBy: paymentData.receivedBy,
      });

      handleClosePaymentModal();
      await fetchAllData();
      toast.success("Payment recorded successfully!");
    } catch (error) {
      console.error("Error recording payment:", error);
      toast.error("Failed to record payment");
    }
    setSubmitting(false);
  };

  const handleOpenInitModal = (student) => {
    setSelectedStudent(student);
    setShowInitModal(true);
  };

  const handleCloseInitModal = () => {
    setShowInitModal(false);
    setSelectedStudent(null);
  };

  const handleInitializeFee = async () => {
    if (!selectedStudent) return;

    setSubmitting(true);
    try {
      await initializeStudentFee(selectedStudent.id, selectedStudent);
      handleCloseInitModal();
      await fetchAllData();
      toast.success(`Fee initialized for ${selectedStudent.name}`);
    } catch (error) {
      console.error("Error initializing fee:", error);
      toast.error("Failed to initialize fee");
    }
    setSubmitting(false);
  };

  const handleOpenDiscountModal = (fee) => {
    setSelectedFee(fee);
    setDiscountData({
      discountType: "percentage",
      discountValue: "",
      discountReason: "",
    });
    setShowDiscountModal(true);
  };

  const handleCloseDiscountModal = () => {
    setShowDiscountModal(false);
    setSelectedFee(null);
    setDiscountData({
      discountType: "percentage",
      discountValue: "",
      discountReason: "",
    });
  };

  const handleApplyDiscount = async (e) => {
    e.preventDefault();

    if (!selectedFee) return;

    setSubmitting(true);
    try {
      await applyDiscount(selectedFee.id, discountData);
      handleCloseDiscountModal();
      await fetchAllData();
    } catch (error) {
      console.error("Error applying discount:", error);
    }
    setSubmitting(false);
  };

  const handleRemoveDiscount = async (feeId) => {
    if (!window.confirm("Are you sure you want to remove this discount?")) {
      return;
    }

    setLoading(true);
    try {
      await removeDiscount(feeId);
      await fetchAllData();
    } catch (error) {
      console.error("Error removing discount:", error);
    }
    setLoading(false);
  };

  // Filter functions
  const getEligibleStudentsWithoutFees = () => {
    const feeStudentIds = studentFees.map(f => f.studentId);
    return students.filter(student => 
      (student.studentLevel === STUDENT_LEVELS.PAID_TRAINEE || 
       (student.studentLevel === STUDENT_LEVELS.INTERN && !student.collegeId)) &&
      !feeStudentIds.includes(student.id)
    );
  };

  const filteredFees = studentFees.filter(fee => {
    const matchesSearch = fee.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fee.studentEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || fee.paymentStatus === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>💳 Fee Management</h2>
        <Button 
          variant="success" 
          onClick={() => fetchAllData()}
          disabled={loading}
        >
          🔄 Refresh
        </Button>
      </div>

      {/* Statistics Cards */}
      {feeStats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center shadow-sm border-primary">
              <Card.Body>
                <DollarSign className="text-primary mb-2" size={32} />
                <h6 className="text-muted">Total Fees</h6>
                <h3 className="text-primary">{formatCurrency(feeStats.totalFees)}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center shadow-sm border-success">
              <Card.Body>
                <TrendingUp className="text-success mb-2" size={32} />
                <h6 className="text-muted">Total Collected</h6>
                <h3 className="text-success">{formatCurrency(feeStats.totalPaid)}</h3>
                <small className="text-muted">{feeStats.collectionRate.toFixed(1)}% collected</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center shadow-sm border-warning">
              <Card.Body>
                <AlertCircle className="text-warning mb-2" size={32} />
                <h6 className="text-muted">Pending Amount</h6>
                <h3 className="text-warning">{formatCurrency(feeStats.totalPending)}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center shadow-sm border-info">
              <Card.Body>
                <Users className="text-info mb-2" size={32} />
                <h6 className="text-muted">Total Students</h6>
                <h3 className="text-info">{feeStats.totalStudents}</h3>
                <div className="mt-2">
                  <Badge bg="success" className="me-1">{feeStats.fullyPaidCount} Paid</Badge>
                  <Badge bg="warning" className="me-1">{feeStats.partiallyPaidCount} Partial</Badge>
                  <Badge bg="danger">{feeStats.overdueCount} Overdue</Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Tabs */}
      <Tabs defaultActiveKey="fees" className="mb-3">
        <Tab eventKey="fees" title={`Student Fees (${studentFees.length})`}>
          <Card className="shadow">
            <Card.Header>
              <Row className="align-items-center">
                <Col md={6}>
                  <h5 className="mb-0">Student Fee Records</h5>
                </Col>
                <Col md={6}>
                  <Row>
                    <Col md={6}>
                      <InputGroup size="sm">
                        <InputGroup.Text>
                          <Search size={16} />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search student..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={6}>
                      <Form.Select
                        size="sm"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="">All Status</option>
                        <option value={PAYMENT_STATUS.PENDING}>Pending</option>
                        <option value={PAYMENT_STATUS.PARTIALLY_PAID}>Partially Paid</option>
                        <option value={PAYMENT_STATUS.FULLY_PAID}>Fully Paid</option>
                        <option value={PAYMENT_STATUS.OVERDUE}>Overdue</option>
                      </Form.Select>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              {filteredFees.length === 0 ? (
                <Alert variant="info" className="text-center">
                  No fee records found. {searchTerm && "Try adjusting your search."}
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Student Name</th>
                        <th>Level</th>
                        <th>Original Fee</th>
                        <th>Discount</th>
                        <th>Total Fee</th>
                        <th>Paid</th>
                        <th>Remaining</th>
                        <th>Installments</th>
                        <th>Status</th>
                        <th>Due Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFees.map((fee, index) => (
                        <tr key={fee.id}>
                          <td>{index + 1}</td>
                          <td>
                            <strong>{fee.studentName}</strong>
                            <br />
                            <small className="text-muted">{fee.studentEmail}</small>
                          </td>
                          <td>
                            <Badge bg="info">
                              {fee.studentLevel.replace(/_/g, " ").toUpperCase()}
                            </Badge>
                          </td>
                          <td>
                            {formatCurrency(fee.originalFee || fee.totalFee)}
                            {fee.discountAmount > 0 && (
                              <>
                                <br />
                                <small className="text-muted text-decoration-line-through">
                                  Original
                                </small>
                              </>
                            )}
                          </td>
                          <td>
                            {fee.discountAmount > 0 ? (
                              <>
                                <Badge bg="success">
                                  {formatCurrency(fee.discountAmount)}
                                </Badge>
                                <br />
                                <small className="text-success">
                                  {fee.discountPercentage?.toFixed(1)}% off
                                </small>
                                {fee.discountReason && (
                                  <>
                                    <br />
                                    <small className="text-muted" title={fee.discountReason}>
                                      💡 {fee.discountReason.substring(0, 20)}...
                                    </small>
                                  </>
                                )}
                              </>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <strong>{formatCurrency(fee.totalFee)}</strong>
                            {fee.discountAmount > 0 && (
                              <>
                                <br />
                                <small className="text-success">After discount</small>
                              </>
                            )}
                          </td>
                          <td className="text-success">
                            <strong>{formatCurrency(fee.paidAmount)}</strong>
                          </td>
                          <td className="text-danger">
                            <strong>{formatCurrency(fee.remainingAmount)}</strong>
                          </td>
                          <td>
                            <Badge bg="secondary">
                              {fee.installmentsPaid}/{fee.maxInstallments}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={getPaymentStatusColor(fee.paymentStatus)}>
                              {getPaymentStatusDisplay(fee.paymentStatus)}
                            </Badge>
                          </td>
                          <td>
                            {fee.dueDate && fee.paymentStatus !== PAYMENT_STATUS.FULLY_PAID ? (
                              <Badge 
                                bg={new Date(fee.dueDate) < new Date() ? "danger" : "warning"}
                              >
                                {new Date(fee.dueDate).toLocaleDateString()}
                              </Badge>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1">
                              {fee.paymentStatus !== PAYMENT_STATUS.FULLY_PAID && (
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() => handleOpenPaymentModal(fee)}
                                  className="w-100"
                                >
                                  💰 Record Payment
                                </Button>
                              )}
                              {fee.paidAmount === 0 && (
                                <>
                                  {fee.discountAmount > 0 ? (
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleRemoveDiscount(fee.id)}
                                      className="w-100"
                                    >
                                      ✖️ Remove Discount
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      onClick={() => handleOpenDiscountModal(fee)}
                                      className="w-100"
                                    >
                                      🎁 Apply Discount
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="payments" title={`Payment History (${allPayments.length})`}>
          <Card className="shadow">
            <Card.Header>
              <h5 className="mb-0">All Payment Records</h5>
            </Card.Header>
            <Card.Body>
              {allPayments.length === 0 ? (
                <Alert variant="info" className="text-center">
                  No payment records found.
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Payment Date</th>
                        <th>Student Name</th>
                        <th>Installment</th>
                        <th>Amount</th>
                        <th>Payment Method</th>
                        <th>Transaction ID</th>
                        <th>Received By</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allPayments.map((payment, index) => (
                        <tr key={payment.id}>
                          <td>{index + 1}</td>
                          <td>
                            {payment.paymentDate
                              ? new Date(payment.paymentDate).toLocaleDateString()
                              : "N/A"}
                          </td>
                          <td>
                            <strong>{payment.studentName}</strong>
                            <br />
                            <small className="text-muted">{payment.studentEmail}</small>
                          </td>
                          <td>
                            <Badge bg="info">#{payment.installmentNumber}</Badge>
                          </td>
                          <td>
                            <strong className="text-success">
                              {formatCurrency(payment.amount)}
                            </strong>
                          </td>
                          <td>
                            <Badge bg="secondary">
                              {payment.paymentMethod.replace(/_/g, " ").toUpperCase()}
                            </Badge>
                          </td>
                          <td>
                            <small className="text-muted">{payment.transactionId}</small>
                          </td>
                          <td>{payment.receivedBy || "N/A"}</td>
                          <td>
                            <small>{payment.notes || "-"}</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab 
          eventKey="initialize" 
          title={`Initialize Fees (${getEligibleStudentsWithoutFees().length})`}
        >
          <Card className="shadow">
            <Card.Header>
              <h5 className="mb-0">Students Eligible for Fee Initialization</h5>
            </Card.Header>
            <Card.Body>
              {getEligibleStudentsWithoutFees().length === 0 ? (
                <Alert variant="success" className="text-center">
                  All eligible students have fee records initialized! ✓
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Student Name</th>
                        <th>Email</th>
                        <th>Level</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getEligibleStudentsWithoutFees().map((student, index) => (
                        <tr key={student.id}>
                          <td>{index + 1}</td>
                          <td><strong>{student.name}</strong></td>
                          <td>{student.email}</td>
                          <td>
                            <Badge bg="warning">
                              {student.studentLevel.replace(/_/g, " ").toUpperCase()}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleOpenInitModal(student)}
                            >
                              🎯 Initialize Fee
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Payment Modal */}
      <Modal show={showPaymentModal} onHide={handleClosePaymentModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>💰 Record Payment</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePaymentSubmit}>
          <Modal.Body>
            {selectedFee && (
              <>
                <Alert variant="info">
                  <strong>Student:</strong> {selectedFee.studentName} ({selectedFee.studentEmail})
                  <br />
                  <strong>Total Fee:</strong> {formatCurrency(selectedFee.totalFee)}
                  <br />
                  <strong>Remaining Balance:</strong> {formatCurrency(selectedFee.remainingAmount)}
                  <br />
                  <strong>Next Installment:</strong> {formatCurrency(selectedFee.installmentAmount)} 
                  (Installment {selectedFee.installmentsPaid + 1}/{selectedFee.maxInstallments})
                </Alert>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Amount *</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>₹</InputGroup.Text>
                        <Form.Control
                          type="number"
                          step="0.01"
                          placeholder="Enter amount"
                          value={paymentData.amount}
                          onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                          required
                          max={selectedFee.remainingAmount}
                        />
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Maximum: {formatCurrency(selectedFee.remainingAmount)}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Method *</Form.Label>
                      <Form.Select
                        value={paymentData.paymentMethod}
                        onChange={(e) => setPaymentData({...paymentData, paymentMethod: e.target.value})}
                        required
                      >
                        <option value={PAYMENT_METHODS.CASH}>Cash</option>
                        <option value={PAYMENT_METHODS.BANK_TRANSFER}>Bank Transfer</option>
                        <option value={PAYMENT_METHODS.UPI}>UPI</option>
                        <option value={PAYMENT_METHODS.CREDIT_CARD}>Credit Card</option>
                        <option value={PAYMENT_METHODS.DEBIT_CARD}>Debit Card</option>
                        <option value={PAYMENT_METHODS.CHEQUE}>Cheque</option>
                        <option value={PAYMENT_METHODS.OTHER}>Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Transaction ID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Transaction/Reference ID"
                        value={paymentData.transactionId}
                        onChange={(e) => setPaymentData({...paymentData, transactionId: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Received By</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Admin name"
                        value={paymentData.receivedBy}
                        onChange={(e) => setPaymentData({...paymentData, receivedBy: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Additional notes or remarks..."
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClosePaymentModal}>
              Cancel
            </Button>
            <Button variant="success" type="submit" disabled={submitting}>
              {submitting ? "Recording..." : "Record Payment"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Initialize Fee Modal */}
      <Modal show={showInitModal} onHide={handleCloseInitModal}>
        <Modal.Header closeButton>
          <Modal.Title>🎯 Initialize Fee Record</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedStudent && (
            <Alert variant="warning">
              <strong>Confirm Fee Initialization</strong>
              <br />
              <br />
              <strong>Student:</strong> {selectedStudent.name}
              <br />
              <strong>Email:</strong> {selectedStudent.email}
              <br />
              <strong>Level:</strong> {selectedStudent.studentLevel.replace(/_/g, " ").toUpperCase()}
              <br />
              <br />
              A fee record will be created based on the configured fee amount for this student level.
              The student will be able to pay in 2 installments.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseInitModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleInitializeFee} disabled={submitting}>
            {submitting ? "Initializing..." : "Initialize Fee"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Discount Modal */}
      <Modal show={showDiscountModal} onHide={handleCloseDiscountModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>🎁 Apply Discount</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleApplyDiscount}>
          <Modal.Body>
            {selectedFee && (
              <>
                <Alert variant="info">
                  <strong>Student:</strong> {selectedFee.studentName} ({selectedFee.studentEmail})
                  <br />
                  <strong>Original Fee:</strong> {formatCurrency(selectedFee.originalFee || selectedFee.totalFee)}
                  <br />
                  <strong>Current Total:</strong> {formatCurrency(selectedFee.totalFee)}
                  {selectedFee.paidAmount > 0 && (
                    <>
                      <br />
                      <span className="text-danger">
                        ⚠️ Payments already made: {formatCurrency(selectedFee.paidAmount)}. 
                        Discounts can only be applied before any payments.
                      </span>
                    </>
                  )}
                </Alert>

                {selectedFee.paidAmount === 0 && (
                  <>
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Label>Discount Type *</Form.Label>
                        <div className="d-flex gap-3">
                          <Form.Check
                            type="radio"
                            label="Percentage (%)"
                            name="discountType"
                            value="percentage"
                            checked={discountData.discountType === "percentage"}
                            onChange={(e) => setDiscountData({...discountData, discountType: e.target.value})}
                          />
                          <Form.Check
                            type="radio"
                            label="Fixed Amount (₹)"
                            name="discountType"
                            value="fixed"
                            checked={discountData.discountType === "fixed"}
                            onChange={(e) => setDiscountData({...discountData, discountType: e.target.value})}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            {discountData.discountType === "percentage" 
                              ? "Discount Percentage *" 
                              : "Discount Amount *"}
                          </Form.Label>
                          <InputGroup>
                            {discountData.discountType === "percentage" ? (
                              <>
                                <Form.Control
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  placeholder="Enter percentage (0-100)"
                                  value={discountData.discountValue}
                                  onChange={(e) => setDiscountData({...discountData, discountValue: e.target.value})}
                                  required
                                />
                                <InputGroup.Text>%</InputGroup.Text>
                              </>
                            ) : (
                              <>
                                <InputGroup.Text>₹</InputGroup.Text>
                                <Form.Control
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max={selectedFee.totalFee - 1}
                                  placeholder="Enter amount"
                                  value={discountData.discountValue}
                                  onChange={(e) => setDiscountData({...discountData, discountValue: e.target.value})}
                                  required
                                />
                              </>
                            )}
                          </InputGroup>
                          {discountData.discountValue && (
                            <Form.Text className="text-muted">
                              {discountData.discountType === "percentage" 
                                ? `Discount Amount: ${formatCurrency((selectedFee.totalFee * parseFloat(discountData.discountValue || 0)) / 100)}`
                                : `Discount %: ${((parseFloat(discountData.discountValue || 0) / selectedFee.totalFee) * 100).toFixed(1)}%`
                              }
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>New Total Fee</Form.Label>
                          <div className="form-control bg-light">
                            <strong className="text-success">
                              {discountData.discountValue ? (
                                formatCurrency(
                                  discountData.discountType === "percentage"
                                    ? selectedFee.totalFee - ((selectedFee.totalFee * parseFloat(discountData.discountValue || 0)) / 100)
                                    : selectedFee.totalFee - parseFloat(discountData.discountValue || 0)
                                )
                              ) : (
                                formatCurrency(selectedFee.totalFee)
                              )}
                            </strong>
                          </div>
                          <Form.Text className="text-muted">
                            Installment amount: {discountData.discountValue ? (
                              formatCurrency(
                                (discountData.discountType === "percentage"
                                  ? selectedFee.totalFee - ((selectedFee.totalFee * parseFloat(discountData.discountValue || 0)) / 100)
                                  : selectedFee.totalFee - parseFloat(discountData.discountValue || 0)
                                ) / selectedFee.maxInstallments
                              )
                            ) : (
                              formatCurrency(selectedFee.installmentAmount)
                            )}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Reason for Discount</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Explain why this discount is being applied (e.g., early registration, financial hardship, special offer, etc.)"
                        value={discountData.discountReason}
                        onChange={(e) => setDiscountData({...discountData, discountReason: e.target.value})}
                      />
                    </Form.Group>
                  </>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDiscountModal}>
              Cancel
            </Button>
            {selectedFee && selectedFee.paidAmount === 0 && (
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? "Applying..." : "Apply Discount"}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
